/**
 * Between Sessions cohort constants and utilities
 *
 * Cohorts run on a trimester basis (4 months each):
 * - Winter: January 1 - April 30
 * - Spring: May 1 - August 31
 * - Fall: September 1 - December 31
 *
 * Registration closes 1 week before each cohort starts.
 */

/**
 * Shared invite URL for the Between Sessions Slack workspace. Used by
 * the auto-sent Slack invite email + the in-app welcome banner.
 *
 * Slack shared-invite links expire by default; if onboarding emails
 * start bouncing or members report a "this link has expired" page,
 * regenerate from Slack workspace settings ("Invite people → Edit link
 * settings → Never expire") and update this one constant. Both surfaces
 * pick up the new URL on next request.
 */
export const BETWEEN_SESSIONS_SLACK_INVITE_URL =
  'https://join.slack.com/t/between-sessions-alli/shared_invite/zt-3p7j09r3k-N2wnFRO4zp4d~xSQBc60~g';

export type BetweenSessionsSeason = 'winter' | 'spring' | 'fall';

export interface CohortInfo {
  id: string;           // e.g., "26spring"
  name: string;         // e.g., "Spring 2026"
  startDate: Date;
  displayDate: string;  // e.g., "May 1, 2026"
  cutoffDate: Date;     // 1 week before start
  season: BetweenSessionsSeason;
}

// Cohort definitions with start months (0-indexed)
const COHORT_DEFINITIONS: { month: number; season: BetweenSessionsSeason; name: string }[] = [
  { month: 0, season: 'winter', name: 'Winter' },   // January 1
  { month: 4, season: 'spring', name: 'Spring' },   // May 1
  { month: 8, season: 'fall', name: 'Fall' },       // September 1
];

/**
 * Get the next available cohort based on current date.
 *
 * `cutoffDate` (1 week before start) is retained on the returned info
 * so marketing surfaces can display a "registration closed" state and
 * `isPastCutoff()` still works — but selection here rolls off the
 * cohort at its **actual start**, not the cutoff. Rationale: when a
 * member trickles in during the cutoff-to-start window (e.g. Aug 26–
 * 31 for the Sept 1 fall cohort), it's cleaner for the credit
 * allocator + confirm-payment to land them in the imminent cohort
 * than to auto-punt them 4 months out. Any "signups closed" gating
 * is now a product decision at the marketing layer, not an implicit
 * downstream consequence of this function's return value.
 */
export function getNextCohort(currentDate: Date = new Date()): CohortInfo {
  const year = currentDate.getFullYear();

  // Check each cohort in order for the current year and next year
  for (let yearOffset = 0; yearOffset <= 1; yearOffset++) {
    const checkYear = year + yearOffset;
    for (const cohort of COHORT_DEFINITIONS) {
      const startDate = new Date(checkYear, cohort.month, 1);
      const cutoffDate = new Date(startDate);
      cutoffDate.setDate(cutoffDate.getDate() - 7); // 1 week before

      // Roll off the cohort only once its startDate has passed — so
      // late signups in the cutoff→start window (e.g. Aug 26 for a
      // Sept 1 cohort) still land in the imminent cohort rather than
      // getting punted forward. Cutoff is still returned on the info
      // object so the marketing / registration-closed banners can
      // reference it independently.
      if (currentDate < startDate) {
        const yearShort = checkYear.toString().slice(-2);
        return {
          id: `${yearShort}${cohort.season}`,
          name: `${cohort.name} ${checkYear}`,
          startDate,
          displayDate: startDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          }),
          cutoffDate,
          season: cohort.season,
        };
      }
    }
  }

  // Fallback to next year's winter if somehow we're past all cohorts
  const nextYear = year + 2;
  const startDate = new Date(nextYear, 0, 1);
  const cutoffDate = new Date(startDate);
  cutoffDate.setDate(cutoffDate.getDate() - 7);
  return {
    id: `${nextYear.toString().slice(-2)}winter`,
    name: `Winter ${nextYear}`,
    startDate,
    displayDate: startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }),
    cutoffDate,
    season: 'winter',
  };
}

/**
 * Get the cohort that's CURRENTLY running, based on the date. Used for
 * Core signups where billing starts immediately and the member is
 * joining the in-progress cohort, not waiting for the next one.
 *
 * Cohort windows: Winter Jan–Apr, Spring May–Aug, Fall Sep–Dec.
 */
export function getCurrentCohort(currentDate: Date = new Date()): CohortInfo {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Walk the definitions in reverse so the latest start month that
  // is <= current month wins.
  let active = COHORT_DEFINITIONS[0]!;
  for (const def of COHORT_DEFINITIONS) {
    if (def.month <= month) active = def;
  }

  const startDate = new Date(year, active.month, 1);
  const cutoffDate = new Date(startDate);
  cutoffDate.setDate(cutoffDate.getDate() - 7);
  return {
    id: `${year.toString().slice(-2)}${active.season}`,
    name: `${active.name} ${year}`,
    startDate,
    displayDate: startDate.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }),
    cutoffDate,
    season: active.season,
  };
}

/**
 * End date of the given cohort: last day of the 4-month season.
 * Fall: Dec 31, Winter: Apr 30, Spring: Aug 31.
 */
export function getCohortEndDate(cohort: CohortInfo): Date {
  const year = cohort.startDate.getFullYear();
  const startMonth = cohort.startDate.getMonth();
  // Last day of the 4th month = day 0 of month +4 (JS Date rolls back to
  // last day of previous month when day=0).
  return new Date(year, startMonth + 4, 0);
}

/**
 * Get upcoming cohorts (excludes the current/next cohort)
 */
export function getUpcomingCohorts(count: number = 3, currentDate: Date = new Date()): CohortInfo[] {
  const cohorts: CohortInfo[] = [];
  let checkDate = new Date(currentDate);

  // Get current cohort first
  const currentCohort = getNextCohort(checkDate);

  // Move past current cohort's start date to find subsequent ones
  checkDate = new Date(currentCohort.startDate);
  checkDate.setDate(checkDate.getDate() + 1);

  for (let i = 0; i < count; i++) {
    const cohort = getNextCohort(checkDate);
    cohorts.push(cohort);
    // Move past this cohort to find the next one
    checkDate = new Date(cohort.startDate);
    checkDate.setDate(checkDate.getDate() + 1);
  }

  return cohorts;
}

/**
 * Get all cohorts starting from the next available one
 */
export function getAllUpcomingCohorts(count: number = 4, currentDate: Date = new Date()): CohortInfo[] {
  const nextCohort = getNextCohort(currentDate);
  const futureCohorts = getUpcomingCohorts(count - 1, currentDate);
  return [nextCohort, ...futureCohorts];
}

/**
 * Get cohort names as simple strings (for dropdowns)
 */
export function getUpcomingCohortNames(count: number = 4, currentDate: Date = new Date()): string[] {
  return getAllUpcomingCohorts(count, currentDate).map(c => c.name);
}

/**
 * Format a cohort ID from season and year
 * e.g., ('spring', 2026) => '26spring'
 */
export function formatCohortId(season: BetweenSessionsSeason, year: number): string {
  const yearShort = year.toString().slice(-2);
  return `${yearShort}${season}`;
}

/**
 * Format a cohort display name from season and year
 * e.g., ('spring', 2026) => 'Spring 2026'
 */
export function formatCohortName(season: BetweenSessionsSeason, year: number): string {
  const seasonName = season.charAt(0).toUpperCase() + season.slice(1);
  return `${seasonName} ${year}`;
}

/**
 * Parse a cohort name into season and year
 * e.g., 'Spring 2026' => { season: 'spring', year: 2026 }
 */
export function parseCohortName(cohortName: string): { season: BetweenSessionsSeason; year: number } | null {
  const match = cohortName.match(/^(Winter|Spring|Fall)\s+(\d{4})$/i);
  if (!match || !match[1] || !match[2]) return null;
  return {
    season: match[1].toLowerCase() as BetweenSessionsSeason,
    year: parseInt(match[2], 10),
  };
}

/**
 * Parse a cohort ID into season and year
 * e.g., '26spring' => { season: 'spring', year: 2026 }
 */
export function parseCohortId(cohortId: string): { season: BetweenSessionsSeason; year: number } | null {
  const match = cohortId.match(/^(\d{2})(winter|spring|fall)$/i);
  if (!match || !match[1] || !match[2]) return null;
  const yearShort = parseInt(match[1], 10);
  // Assume 20XX for now (will need updating in 2100!)
  const year = 2000 + yearShort;
  return {
    season: match[2].toLowerCase() as BetweenSessionsSeason,
    year,
  };
}

/**
 * Check if a cohort is still open for registration
 */
export function isCohortOpenForRegistration(cohortName: string, date: Date = new Date()): boolean {
  const parsed = parseCohortName(cohortName);
  if (!parsed) return false;

  const nextCohort = getNextCohort(date);
  const nextParsed = parseCohortName(nextCohort.name);
  if (!nextParsed) return false;

  // Season order for comparison
  const SEASON_ORDER: BetweenSessionsSeason[] = ['winter', 'spring', 'fall'];

  // A cohort is open if it's the next cohort or a future one
  if (parsed.year > nextParsed.year) return true;
  if (parsed.year < nextParsed.year) return false;

  // Same year - compare season order
  const parsedIdx = SEASON_ORDER.indexOf(parsed.season);
  const nextIdx = SEASON_ORDER.indexOf(nextParsed.season);

  return parsedIdx >= nextIdx;
}

/**
 * Get the start date for a given season and year
 */
export function getCohortStartDate(season: BetweenSessionsSeason, year: number): Date {
  const monthMap: Record<BetweenSessionsSeason, number> = {
    winter: 0,  // January
    spring: 4,  // May
    fall: 8,    // September
  };
  // UTC anchor — a bare `new Date(y, m, 1)` binds to the server's local
  // timezone, so rendering with { timeZone: 'America/Toronto' } drifts
  // "Sept 1 UTC-hosted midnight" back to "Aug 31 20:00 EDT". Using
  // UTC.midnight and formatting in Toronto gives a stable "Sept 1".
  return new Date(Date.UTC(year, monthMap[season], 1));
}

/**
 * Season active for a given calendar month (0-indexed). Winter Jan-Apr /
 * Spring May-Aug / Fall Sep-Dec. Shared here because both the
 * BS_SUPERVISION contract's stamped start date and the /api/me/agreements
 * fallback need the same mapping.
 */
export function seasonForMonth(month: number): BetweenSessionsSeason {
  if (month <= 3) return 'winter';
  if (month <= 7) return 'spring';
  return 'fall';
}

/**
 * Cohort start date (as YYYY-MM-DD) active on a reference date. Used at
 * BS_SUPERVISION create time to stamp `data.startDate`, and as the
 * fallback for legacy rows without that field.
 */
export function computeBsStartDateFrom(referenceDate: Date): string {
  const season = seasonForMonth(referenceDate.getUTCMonth());
  const year = referenceDate.getUTCFullYear();
  return getCohortStartDate(season, year).toISOString().slice(0, 10);
}

/**
 * Resolve a BS_SUPERVISION agreement's supervision start date. Prefers
 * the persisted `data.startDate` (stamped at create/sign time); falls
 * back to the cohort active at `createdAt` for legacy rows where `data`
 * is null. Returns an ISO YYYY-MM-DD string.
 *
 * The date MUST be historically stable — early versions of
 * /api/me/agreements re-derived it from today's cohort per request, so
 * an agreement signed in May would flip to "Sept 1" the moment the
 * fall cohort rolled over.
 */
export function resolveBsAgreementStartDate(
  data: unknown,
  createdAt: Date | string,
): string {
  const persisted =
    data && typeof data === 'object' && 'startDate' in data
      ? (data as { startDate?: unknown }).startDate
      : null;
  if (typeof persisted === 'string' && persisted.length > 0) return persisted;
  const ref = createdAt instanceof Date ? createdAt : new Date(createdAt);
  return computeBsStartDateFrom(ref);
}

/**
 * Format a YYYY-MM-DD string as "September 1, 2026" (long month) or
 * "Sept 1, 2026" (short). UTC-anchored so a bare date isn't shifted by
 * the viewer's local offset. Empty input → empty string.
 */
export function formatCohortDate(
  iso: string | null | undefined,
  style: 'short' | 'long' = 'short',
): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  return new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])))
    .toLocaleDateString('en-US', {
      month: style,
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    });
}

/**
 * Get the registration cutoff date (1 week before start)
 */
export function getCohortCutoffDate(season: BetweenSessionsSeason, year: number): Date {
  const startDate = getCohortStartDate(season, year);
  const cutoffDate = new Date(startDate);
  cutoffDate.setDate(cutoffDate.getDate() - 7);
  return cutoffDate;
}

// ==================== ALLI PRACTICUM COHORTS ====================

/**
 * Alli Practicum cohort info
 * Follows same Winter → Spring → Fall cycle
 * Started Fall 2023
 */
export interface AlliPracticumCohortInfo {
  id: string;       // e.g., "24spring"
  name: string;     // e.g., "Spring 2024"
  season: BetweenSessionsSeason;
  year: number;
}

// Alli Practicum started Fall 2023
const ALLI_PRACTICUM_START_YEAR = 2023;
const ALLI_PRACTICUM_START_SEASON: BetweenSessionsSeason = 'fall';

/**
 * Get all Alli Practicum cohorts from Fall 2023 up to the most recently started one
 */
export function getAlliPracticumCohorts(currentDate: Date = new Date()): AlliPracticumCohortInfo[] {
  const cohorts: AlliPracticumCohortInfo[] = [];
  const seasons: BetweenSessionsSeason[] = ['winter', 'spring', 'fall'];

  let year = ALLI_PRACTICUM_START_YEAR;
  let seasonIdx = seasons.indexOf(ALLI_PRACTICUM_START_SEASON);

  // Generate cohorts until we reach one that hasn't started yet
  while (true) {
    const season = seasons[seasonIdx] as BetweenSessionsSeason;
    const startDate = getCohortStartDate(season, year);

    // Stop if this cohort hasn't started yet
    if (startDate > currentDate) break;

    const yearShort = year.toString().slice(-2);
    cohorts.push({
      id: `${yearShort}${season}`,
      name: formatCohortName(season, year),
      season: season,
      year,
    });

    // Move to next season
    seasonIdx++;
    if (seasonIdx >= seasons.length) {
      seasonIdx = 0;
      year++;
    }

    // Safety limit to prevent infinite loops
    if (cohorts.length > 50) break;
  }

  return cohorts;
}

/**
 * Get Alli Practicum cohort IDs as strings (for dropdowns)
 * Returns in chronological order
 */
export function getAlliPracticumCohortIds(currentDate: Date = new Date()): string[] {
  return getAlliPracticumCohorts(currentDate).map(c => c.id);
}

/**
 * Get Alli Practicum cohort names as strings (for display)
 * Returns in chronological order
 */
export function getAlliPracticumCohortNames(currentDate: Date = new Date()): string[] {
  return getAlliPracticumCohorts(currentDate).map(c => c.name);
}

// Legacy static export for backwards compatibility
export const ALLI_PRACTICUM_COHORTS = [
  '23fall',
  '23winter',
  '23spring',
  '24fall',
  '24winter',
  '24spring',
  '25fall',
  '25winter',
  '25spring',
] as const;

export type AlliPracticumCohort = typeof ALLI_PRACTICUM_COHORTS[number];
